import React, { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import ChartFrame, { CHART_PALETTE, TOOLTIP_STYLE } from './ChartFrame';
import { GapNotice } from '../states';
import { PMS_LABELS, fmtNumber } from '../derive';

// PMS distribution. Bars are clickable to drill into a PMS via the drawer.
// When backend §B is live, the table column "Properties" lights up; until
// then, the chart still shows host counts (always available).
export default function CanvasPms({ filteredHosts, propertiesByPms, propertiesAvailable, onSelectPms }) {
  const data = useMemo(() => {
    const m = new Map();
    filteredHosts.forEach((h) => {
      const k = h.pmsType || 'unknown';
      m.set(k, (m.get(k) || 0) + 1);
    });
    return Array.from(m.entries())
      .map(([k, count]) => ({
        id: k,
        label: PMS_LABELS[k] || k,
        count,
        properties: propertiesByPms?.get(k) || 0,
      }))
      .sort((a, b) => b.count - a.count);
  }, [filteredHosts, propertiesByPms]);

  const totalHosts = data.reduce((s, d) => s + d.count, 0);
  const totalProps = data.reduce((s, d) => s + d.properties, 0);
  const hasUnknown = data.some((d) => d.id === 'unknown');

  return (
    <ChartFrame
      title="Hosts by PMS"
      subtitle={
        propertiesAvailable
          ? `${fmtNumber(totalHosts)} hosts · ${fmtNumber(totalProps)} properties`
          : `${fmtNumber(totalHosts)} hosts`
      }
      right={hasUnknown ? <GapNotice label="Awaiting §A" /> : null}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 10, right: 8, bottom: 0, left: -16 }}>
          <CartesianGrid stroke="#f4f4f5" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#71717a' }} />
          <YAxis tick={{ fontSize: 10, fill: '#71717a' }} allowDecimals={false} />
          <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'rgba(99,102,241,0.06)' }} />
          <Bar dataKey="count" radius={[4, 4, 0, 0]} isAnimationActive={false}>
            {data.map((d, i) => (
              <Cell
                key={d.id}
                fill={CHART_PALETTE[i % CHART_PALETTE.length]}
                cursor="pointer"
                onClick={() => onSelectPms?.(d.id)}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartFrame>
  );
}
